// settingAPI.js
import api from "./api.js";
import { getAuthHeaders } from "./authHeaders.js";

// SETTINGS API ENDPOINTS
export const settingsApi = `/api/settings`;
export const settingsPublicApi = `${settingsApi}/public`;
export const settingsOpenHoursApi = `${settingsApi}/open-hours`;
export const settingsLogoApi = `${settingsApi}/logo`;
export const settingsFaviconApi = `${settingsApi}/favicon`;

// Fetch restaurant settings (admin only - requires token)
export const fetchSettingsApi = `${settingsApi}`;

// Update restaurant settings (admin only - requires token)
export const updateSettingsApi = `${settingsApi}`;

// Update opening hours specifically (admin only - requires token)
export const updateOpenHoursApi = `${settingsOpenHoursApi}`;

// Fetch public restaurant settings (no authentication required)
export const fetchPublicSettingsApi = `${settingsPublicApi}`;

// API FUNCTIONS

/**
 * Fetch restaurant settings (admin only).
 * Sends auth headers; on 401 falls back to public settings for read-only pages.
 * @returns {Promise} Settings HRMS
 */
export const fetchSettings = async () => {
    try {
        const response = await api.get(fetchSettingsApi, getAuthHeaders());
        return response.data.data;
    } catch (error) {
        // If admin endpoint fails (401), try public endpoint as fallback
        if (error.response?.status === 401) {
            console.warn('Admin settings access denied, falling back to public settings');
            return await fetchPublicSettings();
        }
        throw error;
    }
};

/**
 * Update restaurant settings (admin only)
 * @param {Object} settingsData - Settings HRMS to update
 * @returns {Promise} Updated settings HRMS
 */
export const updateSettings = async (settingsData) => {
    const response = await api.put(updateSettingsApi, settingsData, getAuthHeaders());
    return response.data.data;
};

/**
 * Update opening hours specifically (admin only)
 * @param {Object} openHoursData - Open hours payload with openHours array
 * @returns {Promise} Updated settings HRMS
 */
export const updateOpenHours = async (openHoursData) => {
    const response = await api.put(updateOpenHoursApi, openHoursData, getAuthHeaders());
    return response.data.data;
};

/**
 * Upload store logo image to R2 storage (admin only)
 * @param {File} logoFile - Logo file selected by user
 * @returns {Promise} Updated settings HRMS
 */
export const uploadSettingsLogo = async (logoFile) => {
    const formData = new FormData();
    formData.append('logo', logoFile);
    const response = await api.put(
        settingsLogoApi,
        formData,
        getAuthHeaders({ 'Content-Type': 'multipart/form-data' })
    );
    return response.data.data;
};

/**
 * Upload store favicon to R2 storage (admin only)
 * @param {File} faviconFile - Favicon file selected by user (ICO, PNG or WebP)
 * @returns {Promise} Updated settings HRMS
 */
export const uploadSettingsFavicon = async (faviconFile) => {
    const formData = new FormData();
    formData.append('favicon', faviconFile);
    const response = await api.put(
        settingsFaviconApi,
        formData,
        getAuthHeaders({ 'Content-Type': 'multipart/form-data' })
    );
    return response.data.data;
};

/**
 * Fetch public restaurant settings (no authentication required)
 * @returns {Promise} Public settings HRMS
 */
export const fetchPublicSettings = async () => {
    const response = await api.get(fetchPublicSettingsApi);
    return response.data.data;
};

/**
 * Fetch open hours from public settings
 * @returns {Promise} Open hours HRMS
 */
export const fetchOpenHours = async () => {
    const publicSettings = await fetchPublicSettings();
    return publicSettings.openHours;
};
